<template>
  <div class="ci-pix" :style="cssVars">

    <!-- Gerando -->
    <div v-if="flow === 'generating'" class="ci-pix__loading">
      <div class="ci-pix__spinner">
        <span class="spinner-border"></span>
      </div>
      <p class="ci-pix__loading-label">Gerando QR Code Pix…</p>
    </div>

    <!-- Aguardando pagamento -->
    <div v-else-if="flow === 'paying'" class="ci-pix__paying">
      <!-- Valor -->
      <div class="ci-pix__amount-block">
        <span class="ci-pix__amount-label">Valor a pagar</span>
        <span class="ci-pix__amount-value">R$ {{ formattedTotal }}</span>
      </div>

      <!-- QR code -->
      <div class="ci-pix__qr-wrap">
        <div class="ci-pix__qr-frame">
          <img
            :src="`data:image/png;base64,${pixData.qrcode_image}`"
            alt="QR Code Pix"
            class="ci-pix__qr-img"
          />
        </div>
        <p class="ci-pix__qr-hint">
          <i class="fas fa-mobile-screen-button me-1"></i>Escaneie com seu app bancário
        </p>
      </div>

      <!-- Divider -->
      <div class="ci-pix__divider"><span>ou copie a chave</span></div>

      <!-- Chave Pix -->
      <button class="ci-pix__copy-btn" @click="copyPix">
        <span class="ci-pix__copy-key">{{ pixData.pix_key }}</span>
        <span class="ci-pix__copy-label">
          <i class="fas fa-copy me-1"></i>{{ copied ? 'Copiado!' : 'Copiar' }}
        </span>
      </button>

      <!-- Countdown -->
      <div class="ci-pix__countdown">
        <div class="ci-pix__countdown-bar">
          <div
            class="ci-pix__countdown-fill"
            :style="{ width: `${((POLLING_INTERVAL - countdown) / POLLING_INTERVAL) * 100}%` }"
          ></div>
        </div>
        <div class="ci-pix__countdown-text">
          <span class="ci-pix__dot"></span>
          <span>Aguardando pagamento — verificando em <strong>{{ countdown }}s</strong></span>
        </div>
      </div>

    </div>

    <!-- Sucesso -->
    <div v-else-if="flow === 'success'" class="ci-pix__success">
      <div class="ci-pix__success-icon">
        <i class="fas fa-circle-check"></i>
      </div>
      <h3 class="ci-pix__success-title">Pagamento confirmado!</h3>
      <p class="ci-pix__success-sub">Seu presente foi registrado com sucesso 🎉</p>
    </div>

    <!-- Expirado -->
    <div v-else-if="flow === 'expired'" class="ci-pix__state-box">
      <i class="fas fa-clock ci-pix__state-icon ci-pix__state-icon--warn"></i>
      <h4>Pix expirado</h4>
      <p>A validade expirou. Gere um novo para continuar.</p>
      <button class="ci-pix__retry-btn" @click="generate">Gerar novo Pix</button>
    </div>

    <!-- Erro -->
    <div v-else-if="flow === 'error'" class="ci-pix__state-box">
      <i class="fas fa-circle-xmark ci-pix__state-icon ci-pix__state-icon--err"></i>
      <template v-if="errorDetail?.action === 'contact_support'">
        <h4>Pix indisponível</h4>
        <p>{{ errorDetail.message || 'Tente novamente mais tarde ou use outro método.' }}</p>
        <button class="ci-pix__back-btn" @click="$emit('back')">
          <i class="fas fa-chevron-left me-1"></i>Voltar
        </button>
      </template>
      <template v-else>
        <h4>Erro ao gerar Pix</h4>
        <p>{{ errorDetail?.message || 'Não foi possível gerar a chave. Tente novamente.' }}</p>
        <button class="ci-pix__retry-btn" @click="generate">Tentar novamente</button>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import api from '@/services/api';
import { toast } from 'vue3-toastify';
import { formatCurrency } from '@/utils/currencyFormatter';

const props = defineProps({
  eventId:      { type: String, required: true },
  customer:     { type: Object, required: true },
  items:        { type: Array, default: () => [] },
  total:        { type: Number, default: 0 },
  primaryColor: { type: String, default: '#ff50a3' },
});

const cssVars = computed(() => ({ '--ci-primary': props.primaryColor }));

const emit = defineEmits(['success', 'back']);

// ── State ─────────────────────────────────────────────────────────
const flow = ref('generating'); // generating | paying | success | expired | error
const errorDetail = ref(null);
const copied = ref(false);

const pixData = reactive({ qrcode_image: '', pix_key: '', payment_id: '' });

const POLLING_INTERVAL = 15;
const MAX_POLL_ATTEMPTS = 40;
let pollAttempts = 0;
let pixInterval = null;
let countdownInterval = null;
const countdown = ref(POLLING_INTERVAL);

const formattedTotal = computed(() => formatCurrency(props.total));

// ── Generate ──────────────────────────────────────────────────────
async function generate() {
  if (!props.customer) return;
  flow.value = 'generating';
  clearIntervals();
  errorDetail.value = null;

  const payload = {
    event_id:          props.eventId,
    items:             props.items.map(i => ({ id: i.id, price: i.price, quantity: i.quantity || 1 })),
    total:             props.total,
    method:            'pix',
    customer_name:     props.customer.name,
    customer_cpf_cnpj: props.customer.cpf_cnpj,
    customer_email:    props.customer.email,
    customer_phone:    props.customer.phone,
  };

  try {
    const { data } = await api.post(`/payments/pix/${props.eventId}`, payload);
    pixData.qrcode_image = data.qrcode_image;
    pixData.pix_key      = data.pix_key;
    pixData.payment_id   = data.id;
    flow.value = 'paying';
    startPolling();
  } catch (err) {
    const respData = err?.response?.data;
    errorDetail.value = null;
    if (respData) {
      const gwErrors = respData.gateway_errors || [];
      const supportErr = gwErrors.find(e => e.action === 'contact_support');
      if (supportErr) {
        errorDetail.value = { action: 'contact_support', message: supportErr.message || respData.message || '' };
      } else if (respData.message) {
        errorDetail.value = { action: '', message: respData.message };
      }
    }
    toast.error(errorDetail.value?.message || 'Não foi possível gerar o Pix. Tente novamente.');
    flow.value = 'error';
  }
}

// ── Polling ───────────────────────────────────────────────────────
function startPolling() {
  clearIntervals();
  countdown.value = POLLING_INTERVAL;
  pollAttempts = 0;

  countdownInterval = setInterval(() => {
    countdown.value = Math.max(0, countdown.value - 1);
  }, 1000);

  pixInterval = setInterval(checkPayment, POLLING_INTERVAL * 1000);
}

async function checkPayment() {
  pollAttempts++;
  if (pollAttempts >= MAX_POLL_ATTEMPTS) {
    clearIntervals();
    flow.value = 'expired';
    return;
  }
  countdown.value = POLLING_INTERVAL;

  try {
    const { data } = await api.get(`/payments/status/${pixData.payment_id}`);
    if (data.status === 'paid' || data.status === 'confirmed') {
      clearIntervals();
      flow.value = 'success';
      emit('success');
    } else if (['cancelled', 'failed', 'refunded', 'chargeback'].includes(data.status)) {
      clearIntervals();
      flow.value = 'expired';
    }
  } catch {
    // silent retry
  }
}

function clearIntervals() {
  if (pixInterval) { clearInterval(pixInterval); pixInterval = null; }
  if (countdownInterval) { clearInterval(countdownInterval); countdownInterval = null; }
}

// ── Copy ──────────────────────────────────────────────────────────
async function copyPix() {
  try {
    await navigator.clipboard.writeText(pixData.pix_key);
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 2000);
  } catch {
    toast.warning('Não foi possível copiar. Copie manualmente.', { autoClose: 2000 });
  }
}

onMounted(generate);
onBeforeUnmount(clearIntervals);
</script>

<style scoped>
.ci-pix {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0;
  width: 100%;
}

/* ── Loading ── */
.ci-pix__loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2.5rem 1rem;
  gap: 1rem;
}
.ci-pix__spinner .spinner-border {
  width: 2.5rem;
  height: 2.5rem;
  border-color: var(--ci-primary, #ff50a3);
  border-right-color: transparent;
}
.ci-pix__loading-label {
  color: rgba(255,255,255,0.5);
  font-size: 0.85rem;
  margin: 0;
}

/* ── Paying ── */
.ci-pix__paying {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 0.85rem;
}

.ci-pix__amount-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
}
.ci-pix__amount-label {
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.4);
}
.ci-pix__amount-value {
  font-size: 1.6rem;
  font-weight: 800;
  color: #fff;
}

.ci-pix__qr-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}
.ci-pix__qr-frame {
  background: #fff;
  border-radius: 0.75rem;
  padding: 0.75rem;
  box-shadow: 0 4px 24px rgba(0,0,0,0.4);
}
.ci-pix__qr-img {
  width: 160px;
  height: 160px;
  display: block;
}
.ci-pix__qr-hint {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.4);
  margin: 0;
}

.ci-pix__divider {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255,255,255,0.25);
  font-size: 0.72rem;
}
.ci-pix__divider::before,
.ci-pix__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: rgba(255,255,255,0.12);
}

.ci-pix__copy-btn {
  width: 100%;
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 0.65rem;
  padding: 0.65rem 0.85rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  cursor: pointer;
  transition: background 0.18s;
}
.ci-pix__copy-btn:hover { background: rgba(255,255,255,0.11); }
.ci-pix__copy-key {
  font-size: 0.75rem;
  color: rgba(255,255,255,0.6);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  text-align: left;
}
.ci-pix__copy-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--ci-primary, #ff50a3);
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Countdown ── */
.ci-pix__countdown {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.ci-pix__countdown-bar {
  width: 100%;
  height: 3px;
  background: rgba(255,255,255,0.1);
  border-radius: 999px;
  overflow: hidden;
}
.ci-pix__countdown-fill {
  height: 100%;
  background: var(--ci-primary, #ff50a3);
  border-radius: 999px;
  transition: width 1s linear;
}
.ci-pix__countdown-text {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.72rem;
  color: rgba(255,255,255,0.4);
}
.ci-pix__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4ade80;
  animation: pulse-dot 1.5s infinite;
  flex-shrink: 0;
}
@keyframes pulse-dot {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* ── Back btn ── */
.ci-pix__back-btn {
  background: none;
  border: none;
  font-size: 0.78rem;
  color: rgba(255,255,255,0.4);
  cursor: pointer;
  padding: 0.25rem 0;
  transition: color 0.18s;
  align-self: flex-start;
}
.ci-pix__back-btn:hover { color: rgba(255,255,255,0.7); }

/* ── Success ── */
.ci-pix__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  gap: 0.5rem;
  text-align: center;
}
.ci-pix__success-icon {
  font-size: 3rem;
  color: #4ade80;
  margin-bottom: 0.5rem;
}
.ci-pix__success-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: #fff;
  margin: 0;
}
.ci-pix__success-sub {
  font-size: 0.85rem;
  color: rgba(255,255,255,0.5);
  margin: 0;
}

/* ── State box (expired / error) ── */
.ci-pix__state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  gap: 0.5rem;
  text-align: center;
}
.ci-pix__state-box h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
}
.ci-pix__state-box p {
  font-size: 0.82rem;
  color: rgba(255,255,255,0.5);
  margin: 0;
}
.ci-pix__state-icon {
  font-size: 2.5rem;
  margin-bottom: 0.25rem;
}
.ci-pix__state-icon--warn { color: #facc15; }
.ci-pix__state-icon--err  { color: #f87171; }

.ci-pix__retry-btn {
  margin-top: 0.5rem;
  padding: 0.6rem 1.5rem;
  background: var(--ci-primary, #ff50a3);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  border: none;
  border-radius: 0.65rem;
  cursor: pointer;
  transition: opacity 0.18s;
}
.ci-pix__retry-btn:hover { opacity: 0.85; }
</style>
